/**
 * useScreenNavigation Hook
 * 
 * Manages screen navigation, URL synchronization, and breadcrumb tracking.
 * Provides a unified navigation system for the application.
 * 
 * @module useScreenNavigation
 */

import { useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { APP_MESSAGES, SUBJECT_DISPLAY_NAMES } from '../constants/messages';
import { TIMEOUTS } from '../constants/timing';
import { SOUND_TYPES } from '../constants/audio';
import { HAPTIC_TYPES } from '../constants/audio';

/**
 * Hook for managing screen navigation, URL synchronization, and breadcrumbs.
 * 
 * @param {Object} options - Configuration options
 * @param {Object} options.scrollOptimizer - Scroll optimizer instance for smooth scrolling
 * @param {Function} options.playSound - Function to play sound effects
 * @param {Function} options.triggerHaptic - Function to trigger haptic feedback
 * @param {Function} options.setSelectedNewsletter - Setter for newsletter selection
 * @param {Function} options.setSelectedSubject - Setter for subject selection
 * @param {Function} options.setSelectedCategory - Setter for category selection
 * @param {Function} options.setShowSearch - Setter for search visibility
 * @param {Function} options.setSearchQuery - Setter for search query
 * @param {Function} options.setIsLoading - Setter for loading state
 * 
 * @returns {Object} Navigation state and functions
 * @returns {string} returns.currentScreen - Current screen identifier
 * @returns {Array<string>} returns.breadcrumbs - Current breadcrumb trail
 * @returns {Function} returns.navigateTo - Navigate to a screen with optional params
 * @returns {Function} returns.updateBreadcrumbs - Update breadcrumbs manually
 * 
 * @example
 * const { currentScreen, breadcrumbs, navigateTo } = useScreenNavigation({
 *   scrollOptimizer,
 *   playSound,
 *   triggerHaptic,
 *   setSelectedNewsletter,
 *   setSelectedSubject,
 *   setSelectedCategory,
 *   setShowSearch,
 *   setSearchQuery,
 *   setIsLoading
 * });
 * 
 * // Navigate to home
 * navigateTo('home');
 * 
 * // Navigate with params
 * navigateTo('parent-reference', '', { subject: 'phonics', category: 0 });
 */
export const useScreenNavigation = ({
  scrollOptimizer,
  playSound,
  triggerHaptic,
  setSelectedNewsletter,
  setSelectedSubject,
  setSelectedCategory,
  setShowSearch,
  setSearchQuery,
  setIsLoading
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [currentScreen, setCurrentScreen] = useState('home');
  const [breadcrumbs, setBreadcrumbs] = useState([APP_MESSAGES.BREADCRUMB_HOME]);

  /**
   * Breadcrumb navigation helper
   * Updates breadcrumbs based on the current screen
   * 
   * @param {string} newScreen - Screen identifier
   * @param {string} additionalInfo - Additional breadcrumb info
   */
  const updateBreadcrumbs = useCallback((newScreen, additionalInfo = '') => {
    const breadcrumbMap = {
      'home': [APP_MESSAGES.BREADCRUMB_HOME],
      'parent-reference': [APP_MESSAGES.BREADCRUMB_HOME, APP_MESSAGES.BREADCRUMB_PARENT_REFERENCE],
      'achievements': [APP_MESSAGES.BREADCRUMB_HOME, APP_MESSAGES.BREADCRUMB_ACHIEVEMENTS],
      'customize': [APP_MESSAGES.BREADCRUMB_HOME, APP_MESSAGES.BREADCRUMB_CUSTOMIZE],
      'progress': [APP_MESSAGES.BREADCRUMB_HOME, APP_MESSAGES.BREADCRUMB_PROGRESS],
      'feedback': [APP_MESSAGES.BREADCRUMB_HOME, APP_MESSAGES.BREADCRUMB_FEEDBACK],
      'review-mistakes': [APP_MESSAGES.BREADCRUMB_HOME, 'Review Mistakes'],
      'field-trips': [APP_MESSAGES.BREADCRUMB_HOME, APP_MESSAGES.BREADCRUMB_FIELD_TRIPS],
      'phonics': [APP_MESSAGES.BREADCRUMB_HOME, APP_MESSAGES.BREADCRUMB_STUDY_GUIDES, SUBJECT_DISPLAY_NAMES.phonics],
      'math': [APP_MESSAGES.BREADCRUMB_HOME, APP_MESSAGES.BREADCRUMB_STUDY_GUIDES, SUBJECT_DISPLAY_NAMES.math],
      'reading': [APP_MESSAGES.BREADCRUMB_HOME, APP_MESSAGES.BREADCRUMB_STUDY_GUIDES, SUBJECT_DISPLAY_NAMES.reading],
      'spelling': [APP_MESSAGES.BREADCRUMB_HOME, APP_MESSAGES.BREADCRUMB_STUDY_GUIDES, SUBJECT_DISPLAY_NAMES.spelling],
      'science': [APP_MESSAGES.BREADCRUMB_HOME, APP_MESSAGES.BREADCRUMB_STUDY_GUIDES, SUBJECT_DISPLAY_NAMES.science],
      'social': [APP_MESSAGES.BREADCRUMB_HOME, APP_MESSAGES.BREADCRUMB_STUDY_GUIDES, SUBJECT_DISPLAY_NAMES.social],
      'skipcounting': [APP_MESSAGES.BREADCRUMB_HOME, APP_MESSAGES.BREADCRUMB_STUDY_GUIDES, SUBJECT_DISPLAY_NAMES.skipcounting],
      'art': [APP_MESSAGES.BREADCRUMB_HOME, APP_MESSAGES.BREADCRUMB_STUDY_GUIDES, SUBJECT_DISPLAY_NAMES.art],
      'geography': [APP_MESSAGES.BREADCRUMB_HOME, APP_MESSAGES.BREADCRUMB_STUDY_GUIDES, SUBJECT_DISPLAY_NAMES.geography],
      'history': [APP_MESSAGES.BREADCRUMB_HOME, APP_MESSAGES.BREADCRUMB_STUDY_GUIDES, SUBJECT_DISPLAY_NAMES.history]
    };
    
    let newBreadcrumbs = breadcrumbMap[newScreen] || [APP_MESSAGES.BREADCRUMB_HOME];
    if (additionalInfo) {
      newBreadcrumbs.push(additionalInfo);
    }
    setBreadcrumbs(newBreadcrumbs);
  }, []);

  /**
   * Navigation helper with breadcrumb updates and URL updates
   * 
   * @param {string} screen - Target screen identifier
   * @param {string} additionalInfo - Additional breadcrumb info
   * @param {Object} params - Navigation parameters (subject, category, week, etc.)
   */
  const navigateTo = useCallback((screen, additionalInfo = '', params = {}) => {
    if (playSound) {
      playSound(SOUND_TYPES.CLICK);
    }
    if (triggerHaptic) {
      triggerHaptic(HAPTIC_TYPES.LIGHT);
    }

    setCurrentScreen(screen);
    updateBreadcrumbs(screen, additionalInfo);
    
    if (setShowSearch) {
      setShowSearch(false);
    }
    if (setSearchQuery) {
      setSearchQuery('');
    }
    
    // Update URL based on screen and parameters - use HashRouter navigate for all routes
    if (screen === 'home') {
      navigate('/');
    } else if (screen === 'import') {
      navigate('/#import');
    } else if (screen === 'newsletter' || screen.startsWith('newsletter-')) {
      // Always navigate to newsletter selector (PDFs open directly, no individual pages)
      navigate('/newsletter');
      if (setSelectedNewsletter) {
        setSelectedNewsletter(null);
      }
    } else if (screen === 'parent-reference') {
      if (params.subject) {
        if (params.category !== undefined) {
          navigate(`/parent-reference/${params.subject}/${params.category}`);
          if (setSelectedSubject) {
            setSelectedSubject(params.subject);
          }
          if (setSelectedCategory) {
            setSelectedCategory(parseInt(params.category));
          }
        } else {
          navigate(`/parent-reference/${params.subject}`);
          if (setSelectedSubject) {
            setSelectedSubject(params.subject);
          }
          if (setSelectedCategory) {
            setSelectedCategory(0);
          }
        }
      } else {
        navigate('/parent-reference');
        if (setSelectedSubject) {
          setSelectedSubject('phonics');
        }
        if (setSelectedCategory) {
          setSelectedCategory(0);
        }
      }
    } else if (screen === 'calendar') {
      // Calendar supports view modes: /calendar/list, /calendar/month, /calendar/week, /calendar/day
      const viewMode = params.view || 'list';
      navigate(`/calendar/${viewMode}`);
    } else {
      // Use router navigate for all other screens including games
      navigate(`/${screen}`);
    }
    
    // Optimized scrolling with scroll optimizer
    // Always scroll to top after navigation
    if (scrollOptimizer) {
      setTimeout(() => {
        scrollOptimizer.smoothScrollTo(document.body, 0);
        if (setIsLoading) {
          setIsLoading(false);
        }
      }, TIMEOUTS.SCROLL_OPTIMIZE);
    }
  }, [
    navigate,
    scrollOptimizer,
    playSound,
    triggerHaptic,
    updateBreadcrumbs,
    setSelectedNewsletter,
    setSelectedSubject,
    setSelectedCategory,
    setShowSearch,
    setSearchQuery,
    setIsLoading
  ]);

  // Initialize breadcrumbs on component mount
  useEffect(() => {
    updateBreadcrumbs(currentScreen);
  }, [currentScreen, updateBreadcrumbs]);

  // URL synchronization effect
  useEffect(() => {
    // Handle hash-based redirects from 404.html
    if (window.location.hash && window.location.hash.startsWith('#')) {
      const hashPath = window.location.hash.substring(1);
      
      // Skip OAuth callback processing - let AuthCallback handle it
      if (hashPath.includes('access_token') || hashPath.includes('error') || hashPath.includes('auth/callback')) {
        return;
      }
      
      const pathParts = hashPath.split('/').filter(part => part);
      const gameScreens = ['phonics', 'math', 'reading', 'science', 'art', 'geography', 'history', 'spelling'];
      // For game routes (e.g., #/reading) do NOT rewrite the URL. Let the HashRouter handle it.
      if (pathParts[0] && gameScreens.includes(pathParts[0])) {
        setCurrentScreen(pathParts[0]);
        return;
      }
      
      // For non-game routes, update the URL to the correct path without hash
      const basePath = import.meta.env?.VITE_BASE_PATH || '/';
      const pathPrefix = basePath === '/' ? '' : basePath.replace(/\/$/, '');
      window.history.replaceState(null, '', `${pathPrefix}${hashPath}`);
      
      // Process the path
      if (hashPath === '/' || hashPath === '') {
        setCurrentScreen('home');
      } else if (pathParts[0] === 'newsletter') {
        setCurrentScreen('newsletter');
        // Always show newsletter selector (PDFs open directly, no individual pages)
        if (setSelectedNewsletter) {
          setSelectedNewsletter(null);
        }
      } else if (pathParts[0] === 'parent-reference') {
        setCurrentScreen('parent-reference');
        if (pathParts[1]) {
          if (setSelectedSubject) {
            setSelectedSubject(pathParts[1]);
          }
          if (pathParts[2] && setSelectedCategory) {
            setSelectedCategory(parseInt(pathParts[2]));
          } else if (setSelectedCategory) {
            setSelectedCategory(0);
          }
        } else {
          if (setSelectedSubject) {
            setSelectedSubject('phonics');
          }
          if (setSelectedCategory) {
            setSelectedCategory(0);
          }
        }
      } else if (pathParts[0] === 'spelling') {
        setCurrentScreen('spelling');
      } else if (pathParts[0] === 'achievements') {
        setCurrentScreen('achievements');
      } else if (pathParts[0] === 'progress') {
        setCurrentScreen('progress');
      } else if (pathParts[0] === 'customize') {
        setCurrentScreen('customize');
      } else if (pathParts[0] === 'feedback') {
        setCurrentScreen('feedback');
      } else if (pathParts[0] === 'calendar') {
        setCurrentScreen('calendar');
        // Extract view mode from URL (e.g., /calendar/list)
        if (pathParts[1] && ['list', 'month', 'week', 'day'].includes(pathParts[1])) {
          // View mode will be handled by App.jsx via URL sync
        }
      } else if (pathParts[0] === 'field-trips') {
        setCurrentScreen('field-trips');
      }
      return;
    }
    
    const basePath = import.meta.env?.VITE_BASE_PATH || '/';
    const pathToRemove = basePath === '/' ? '' : basePath.replace(/\/$/, '');
    const path = pathToRemove ? location.pathname.replace(pathToRemove, '') : location.pathname;
    const pathParts = path.split('/').filter(part => part);
    
    if (path === '/' || path === '') {
      setCurrentScreen('home');
    } else if (pathParts[0] === 'newsletter') {
      setCurrentScreen('newsletter');
      if (pathParts[1] && setSelectedNewsletter) {
        setSelectedNewsletter(parseInt(pathParts[1]));
      } else if (setSelectedNewsletter) {
        setSelectedNewsletter(null);
      }
    } else if (pathParts[0] === 'parent-reference') {
      setCurrentScreen('parent-reference');
      if (pathParts[1]) {
        if (setSelectedSubject) {
          setSelectedSubject(pathParts[1]);
        }
        if (pathParts[2] && setSelectedCategory) {
          setSelectedCategory(parseInt(pathParts[2]));
        } else if (setSelectedCategory) {
          setSelectedCategory(0);
        }
      } else {
        if (setSelectedSubject) {
          setSelectedSubject('phonics');
        }
        if (setSelectedCategory) {
          setSelectedCategory(0);
        }
      }
    } else if (pathParts[0] === 'spelling') {
      setCurrentScreen('spelling');
    } else if (pathParts[0] === 'achievements') {
      setCurrentScreen('achievements');
    } else if (pathParts[0] === 'progress') {
      setCurrentScreen('progress');
    } else if (pathParts[0] === 'customize') {
      setCurrentScreen('customize');
    } else if (pathParts[0] === 'feedback') {
      setCurrentScreen('feedback');
    } else if (pathParts[0] === 'calendar') {
      setCurrentScreen('calendar');
    } else if (pathParts[0] === 'field-trips') {
      setCurrentScreen('field-trips');
    }
  }, [location.pathname, setSelectedNewsletter, setSelectedSubject, setSelectedCategory]);

  return {
    currentScreen,
    setCurrentScreen,
    breadcrumbs,
    navigateTo,
    updateBreadcrumbs
  };
};

